import { ButtonComponentProps } from "@omni/ui/components/Button/button.props";
import { CallToActionType } from "@omni/ui/components/CallToAction/types/enums/callToActionType.enum";
import { Style } from "@omni/ui/components/CallToAction/types/style";
import { Tracking } from "@omni/ui/components/CallToAction/types/tracking.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, PropsWithChildren, use } from "react";

import { createButtonStyleProperties } from "./helpers/style.helpers";
import {
    createTrackingProperties,
    TrackingProperties
} from "./helpers/tracking.helpers";

type CallToActionContextValue = {
    callToActionType: CallToActionType;
    trackingProperties: {
        click?: TrackingProperties;
        impression?: TrackingProperties;
    };
    styleProperties:
        | Partial<{
              color: ButtonComponentProps["color"];
              outline: ButtonComponentProps["outline"];
              size: ButtonComponentProps["size"];
              fullWidth: ButtonComponentProps["fullWidth"];
              className: string;
          }>
        | undefined;
};

type CallToActionContextProviderProps = PropsWithChildren<{
    callToActionType: CallToActionType;
    tracking: Tracking;
    style: Style;
}>;

export const CallToActionContext = createContext<
    CallToActionContextValue | undefined
>(undefined);
CallToActionContext.displayName = "CallToActionContext";

export const CallToActionContextProvider = ({
    callToActionType,
    style,
    tracking,
    children
}: CallToActionContextProviderProps) => {
    const trackingProperties = {
        click: createTrackingProperties("click", tracking),
        impression: createTrackingProperties("impression", tracking)
    };

    const styleProperties = createButtonStyleProperties(style);

    return (
        <CallToActionContext
            value={{
                callToActionType: callToActionType,
                trackingProperties: trackingProperties,
                styleProperties: styleProperties
            }}
        >
            {children}
        </CallToActionContext>
    );
};

export const useCallToActionContext = () => {
    const context = use<CallToActionContextValue | undefined>(
        CallToActionContext
    );

    if (!isDefined(context)) {
        throw new Error(`${CallToActionContext.displayName} is undefined.`);
    }

    return context;
};
